/*
 * Copyright (c) Dito Technologies
 */


export enum StepStatus {
    SUCCESS,
    ERROR,
    IN_PROGRESS,
    NEEDS_EDIT
}

export enum StepType {
    UPLOAD = 0,
    STAFF_SELECT = 1,
    VERIFY_NOTES = 2,
    DOWNLOAD = 3,
}

export interface State {
    pieceId: string | null,
    selectedBar: string | null;
    deletedBars: string[],
    files: File[];
    selectedStep: StepType;
    uploadStepStatus: StepStatus;
    staffSelectStepStatus: StepStatus;
    downloadStepStatus: StepStatus;
    fileToUploadProgressLookup: {
        [fileName: string]: number | undefined
    };
    barFinderUserConfirmationSuccessPageIds: string[];
    barFinderUserConfirmationLoadingPageIds: string[];
    barFinderUserConfirmationErrorPageIds: string[];
    noteFinderUserConfirmationSuccessPageIds: string[];
    noteFinderUserConfirmationLoadingPageIds: string[];
    noteFinderUserConfirmationErrorPageIds: string[];
    noteFinderUserConfirmationStatus: StepStatus;
}

export interface SetPieceIdAction {
    type: "set_piece_id";
    pieceId: string;
}

export interface SelectStepAction {
    type: "select_step";
    step: StepType;
}

export interface SetUploadStatusAction {
    type: "set_upload_step_status";
    status: StepStatus;
}

export interface SetStaffSelectStepStatusAction {
    type: "set_staff_select_step_status";
    status: StepStatus;
}

export interface SetVerifyNotesStepStatus {
    type: "set_verify_notes_step_status";
    status: StepStatus;
}

export interface AddFilesAction {
    type: "add_files";
    files: FileList;
}

export interface DeleteFileAction {
    type: "delete_file";
    file: File;
}

export interface UploadProgressAction {
    type: "upload_progress";
    fileName: string;
    progress: number;
}

export interface SetSelectedBarAction {
    type: "set_selected_bar";
    barId: string;
}

export interface AddDeletedBarAction {
    type: "add_deleted_bar";
}

export interface BarFinderUserConfirmationAddErrorPageIdAction {
    type: "bar_finder_user_confirmation_add_error_page_id";
    page_id: string;
}

export interface BarFinderUserConfirmationAddLoadingPageIdAction {
    type: "bar_finder_user_confirmation_add_loading_page_id";
    page_id: string;
}

export interface BarFinderUserConfirmationAddSuccessPageIdAction {
    type: "bar_finder_user_confirmation_add_success_page_id";
    page_id: string;
}

export interface NoteFinderUserConfirmationAddErrorPageIdAction {
    type: "note_finder_user_confirmation_add_error_page_id";
    page_id: string;
}

export interface NoteFinderUserConfirmationAddLoadingPageIdAction {
    type: "note_finder_user_confirmation_add_loading_page_id";
    page_id: string;
}

export interface NoteFinderUserConfirmationAddSuccessPageIdAction {
    type: "note_finder_user_confirmation_add_success_page_id";
    page_id: string;
}

export type Action =
    SetUploadStatusAction
    | SetStaffSelectStepStatusAction
    | SelectStepAction
    | AddFilesAction
    | DeleteFileAction
    | UploadProgressAction
    | SetPieceIdAction
    | SetSelectedBarAction
    | AddDeletedBarAction
    | BarFinderUserConfirmationAddErrorPageIdAction
    | BarFinderUserConfirmationAddLoadingPageIdAction
    | BarFinderUserConfirmationAddSuccessPageIdAction
    | NoteFinderUserConfirmationAddErrorPageIdAction
    | NoteFinderUserConfirmationAddLoadingPageIdAction
    | NoteFinderUserConfirmationAddSuccessPageIdAction
    | SetVerifyNotesStepStatus;

export const initialState: State = {
    pieceId: null,
    selectedBar: null,
    barFinderUserConfirmationErrorPageIds: [],
    barFinderUserConfirmationLoadingPageIds: [],
    barFinderUserConfirmationSuccessPageIds: [],
    deletedBars: [],
    selectedStep: StepType.UPLOAD,
    uploadStepStatus: StepStatus.NEEDS_EDIT,
    staffSelectStepStatus: StepStatus.NEEDS_EDIT,
    downloadStepStatus: StepStatus.NEEDS_EDIT,
    files: [],
    fileToUploadProgressLookup: {},
    noteFinderUserConfirmationErrorPageIds: [],
    noteFinderUserConfirmationLoadingPageIds: [],
    noteFinderUserConfirmationSuccessPageIds: [],
    noteFinderUserConfirmationStatus: StepStatus.NEEDS_EDIT
}

export function InitialStateFromPiece(state: State, pieceId: string): State {
    return {
        pieceId: pieceId,
        selectedBar: null,
        barFinderUserConfirmationErrorPageIds: [],
        barFinderUserConfirmationLoadingPageIds: [],
        barFinderUserConfirmationSuccessPageIds: [],
        deletedBars: [],
        selectedStep: StepType.UPLOAD,
        uploadStepStatus: StepStatus.NEEDS_EDIT,
        staffSelectStepStatus: StepStatus.NEEDS_EDIT,
        downloadStepStatus: StepStatus.NEEDS_EDIT,
        files: [],
        fileToUploadProgressLookup: {},
        noteFinderUserConfirmationLoadingPageIds: [],
        noteFinderUserConfirmationSuccessPageIds: [],
        noteFinderUserConfirmationErrorPageIds: [],
        noteFinderUserConfirmationStatus: StepStatus.NEEDS_EDIT
    }
}

export function Reducer(state: State, action: Action): State {
    switch (action.type) {
        case "set_staff_select_step_status":
            return {
                ...state,
                staffSelectStepStatus: action.status,
            };
        case "set_upload_step_status":
            return {
                ...state,
                uploadStepStatus: action.status,
            };
        case "select_step":
            return {
                ...state,
                selectedStep: action.step
            };
        case "add_files":
            return {
                ...state,
                files: [...state.files, ...Array.from(action.files)]
            }
        case "delete_file":
            return {
                ...state,
                files: state.files.filter((file) => file !== action.file)
            }
        case "upload_progress":
            return {
                ...state,
                fileToUploadProgressLookup: {
                    ...state.fileToUploadProgressLookup,
                    [action.fileName]: action.progress
                }
            }
        case "set_piece_id":
            return {
                ...state,
                pieceId: action.pieceId
            }
        case "set_selected_bar":
            return {
                ...state,
                selectedBar: action.barId
            }
        case "add_deleted_bar":
            const selectedBar = state.selectedBar;
            if (!selectedBar) {
                return state;
            }
            return {
                ...state,
                deletedBars: [...state.deletedBars, selectedBar]
            }
        case "bar_finder_user_confirmation_add_success_page_id":
            return {
                ...state,
                barFinderUserConfirmationErrorPageIds: state.barFinderUserConfirmationErrorPageIds.filter((pageId) => pageId !== action.page_id),
                barFinderUserConfirmationLoadingPageIds: state.barFinderUserConfirmationLoadingPageIds.filter((pageId) => action.page_id !== pageId),
                barFinderUserConfirmationSuccessPageIds: [
                    ...state.barFinderUserConfirmationSuccessPageIds,
                    action.page_id
                ]
            }
        case "bar_finder_user_confirmation_add_loading_page_id":
            return {
                ...state,
                barFinderUserConfirmationSuccessPageIds: state.barFinderUserConfirmationSuccessPageIds.filter((pageId) => pageId !== action.page_id),
                barFinderUserConfirmationLoadingPageIds: [
                    ...state.barFinderUserConfirmationLoadingPageIds,
                    action.page_id
                ],
                barFinderUserConfirmationErrorPageIds: [
                    ...state.barFinderUserConfirmationErrorPageIds,
                    action.page_id
                ]
            }
        case "bar_finder_user_confirmation_add_error_page_id":
            return {
                ...state,
                barFinderUserConfirmationErrorPageIds: [
                    ...state.barFinderUserConfirmationErrorPageIds, action.page_id
                ],
                barFinderUserConfirmationLoadingPageIds: state.barFinderUserConfirmationLoadingPageIds.filter((pageId) => pageId !== action.page_id),
                barFinderUserConfirmationSuccessPageIds: state.barFinderUserConfirmationSuccessPageIds.filter((pageId) => pageId !== action.page_id)
            }
        case "note_finder_user_confirmation_add_loading_page_id":
            return {
                ...state,
                noteFinderUserConfirmationLoadingPageIds: [
                    ...state.noteFinderUserConfirmationLoadingPageIds, action.page_id
                ],
                noteFinderUserConfirmationSuccessPageIds: state.noteFinderUserConfirmationSuccessPageIds.filter((pageId) => pageId !== action.page_id),
                noteFinderUserConfirmationErrorPageIds: state.noteFinderUserConfirmationErrorPageIds.filter((pageId) => pageId !== action.page_id)
            }
        case "note_finder_user_confirmation_add_error_page_id":
            return {
                ...state,
                noteFinderUserConfirmationErrorPageIds: [
                    ...state.noteFinderUserConfirmationErrorPageIds, action.page_id
                ],
                noteFinderUserConfirmationSuccessPageIds: state.noteFinderUserConfirmationSuccessPageIds.filter((pageId) => pageId !== action.page_id),
                noteFinderUserConfirmationLoadingPageIds: state.noteFinderUserConfirmationLoadingPageIds.filter((pageId) => pageId !== action.page_id)
            }
        case "note_finder_user_confirmation_add_success_page_id":
            return {
                ...state,
                noteFinderUserConfirmationSuccessPageIds: [
                    ...state.noteFinderUserConfirmationSuccessPageIds, action.page_id
                ],
                noteFinderUserConfirmationErrorPageIds: state.noteFinderUserConfirmationErrorPageIds.filter((pageId) => pageId !== action.page_id),
                noteFinderUserConfirmationLoadingPageIds: state.noteFinderUserConfirmationLoadingPageIds.filter((pageId) => pageId !== action.page_id)
            }
        case "set_verify_notes_step_status":
            return {
                ...state,
                noteFinderUserConfirmationStatus: action.status
            }
        default:
            throw new Error()
    }
}